import { useCallback, useEffect, useState } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import closeSymbol from "@/assets/images/commons/RedCloseX.svg";
import { useAxios } from "@/hooks/use-axios/useAxios";
import ProblemMessage from "@/components/problem-message/ProblemMessage";
import InputText from "@/components/input-text/InputText";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import TopAlertMessageAlertMessage from "@/components/alert-message/TopAlertMessage";
import routes, { buildRoute } from "@/assets/routes/routes";

interface ProjectModalProps {
  inspectionId: number;
  project: string;
  closeModal: () => void;
  getNewProject: (project: string) => void;
}

const ProjectModal = ({
  inspectionId,
  project,
  closeModal,
  getNewProject,
}: ProjectModalProps) => {
  const translate = useTranslate();
  const { request } = useAxios();

  const [newProject, setNewProject] = useState<string>("");
  const [requiredField, setRequiredField] = useState(false);
  const [error, setError] = useState(false);
  const [sameName, setSameName] = useState(false);

  useEffect(() => {
    if (project !== "-") setNewProject(project);
    return () => {
      setNewProject("");
      setRequiredField(false);
      setError(false);
      setSameName(false);
    };
  }, []);

  const changeProject = useCallback((data: string) => setNewProject(data), []);

  const saveNewProject = useCallback(async () => {
    if (!newProject || newProject.trim().length === 0) {
      setRequiredField(true);
    } else {
      setRequiredField(false);
      if (newProject === project) {
        setSameName(true);
      } else {
        try {
          const bodyData = {
            project: newProject,
          };
          const url = buildRoute(routes.updateInpectionId, {
            id: inspectionId,
          });
          const newInspection = await request({
            url: url,
            method: "PATCH",
            body: bodyData,
          });
          if (newInspection?.id) {
            getNewProject(newInspection.project);
          }
        } catch {
          setError(true);
        }
      }
    }
  }, [newProject, project, getNewProject, inspectionId, request]);

  return (
    <div className="fixed overflow-auto bg-ib-black bg-opacity-70 w-[100vw] min-w-[100vw] h-[100vh] min-h-[100vh] top-0 right-0 z-[1000]">
      {sameName ? (
        <TopAlertMessageAlertMessage
          titleLabel="sameProjectTitle"
          formatType="persistent"
          contentType="info"
          textLabel="sameProjectText"
          closeMessage={() => setSameName(false)}
        />
      ) : null}
      {error ? (
        <ProblemMessage
          titleLabel="aProblem"
          error="noPosibleSaveChange"
          closeMessage={() => setError(false)}
        />
      ) : null}
      <div
        className="
        absolute top-[100px] right-1/2 transform translate-x-1/2 bg-ib-white w-fit p-6 rounded-[2px] shadow-modal-1
        max-md:fixed max-[767px]:bottom-0 max-md:top-auto
        max-md:left-0 max-md:right-0 max-md:translate-x-0
        max-md:w-full max-md:rounded-t-xl
        max-md:flex max-md:flex-col max-md:items-center
        "
      >
        <div className="flex flex-row mb-4 justify-between items-center w-[520px] max-w-[calc(100vw-48px)]">
          <div className="font-ib-bold text-[24px] leading-[26px] font-[IberiaHeadlineBold]">
            {translate(project === "-" ? "addProject" : "changeProject")}
          </div>
          <button
            className="h-6 w-6 p-[5px] max-md:absolute max-md:right-6"
            onClick={closeModal}
          >
            <img src={closeSymbol} />
          </button>
        </div>
        <div className="w-full">
          <InputText
            value={newProject}
            onChange={changeProject}
            size="medium"
            label={translate("project")}
            placeholder={translate("project")}
            required={requiredField}
            requiredAsterisk
          />
          <div className=" w-full">
            <PrimaryButton
              colour="red"
              icon="check"
              label={translate("saveChanges")}
              onClick={saveNewProject}
            />
          </div>
          <div className="mt-4 w-full">
            <SecondaryButton
              colour="red"
              label={translate("cancel")}
              onClick={closeModal}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
